import { FC, useState } from 'react'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Integration, IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Button } from '@deepguide-ai/ui/button'
import { Loader2 } from 'lucide-react'
import { APP_NAME } from '@/lib/env'
import { toast } from 'sonner'
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle'

const IntercomIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="intercomGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1F8DED" />
        <stop offset="100%" stopColor="#0056D2" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#intercomGradient)" />
    <path
      d="M8.5 8C8.22386 8 8 8.22386 8 8.5V15.5C8 15.7761 8.22386 16 8.5 16C8.77614 16 9 15.7761 9 15.5V8.5C9 8.22386 8.77614 8 8.5 8ZM12 7.5C11.7239 7.5 11.5 7.72386 11.5 8V16C11.5 16.2761 11.7239 16.5 12 16.5C12.2761 16.5 12.5 16.2761 12.5 16V8C12.5 7.72386 12.2761 7.5 12 7.5ZM15.5 8C15.2239 8 15 8.22386 15 8.5V15.5C15 15.7761 15.2239 16 15.5 16C15.7761 16 16 15.7761 16 15.5V8.5C16 8.22386 15.7761 8 15.5 8Z"
      fill="white"
    />
    <path
      d="M6.00028 5.99998C4.34329 5.99998 3 7.34326 3 8.99998V15C3 16.6567 4.34329 18 6.00028 18H10.2005C10.9259 18 11.6398 18.196 12.2628 18.5695L13.973 19.5933C15.9723 20.7816 18.5 19.3446 18.5 17.0096V8.99998C18.5 7.34326 17.1567 5.99998 15.4997 5.99998H6.00028Z"
      fill="white"
      opacity="0.12"
    />
  </svg>
)

const IntercomConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
}) => {
  const [isConnecting, setIsConnecting] = useState(false)

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      await IntercomProvider.handleConnect({
        workspaceId,
        onSuccess: () => {
          onComplete()
        },
      })
    } catch (error) {
      console.error('Failed to connect to Intercom:', error)
      setIsConnecting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to Intercom</DialogTitle>
          <DialogDescription>
            Connect your Intercom workspace to import help articles and conversations into your documentation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 p-4">
          <div className="text-sm text-muted-foreground">
            You'll be redirected to Intercom to authorize the connection. Make sure pop-ups are enabled for this site.
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isConnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect to Intercom'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const IntercomProvider: IntegrationProvider = {
  id: 'Intercom',
  name: 'Intercom',
  description: `Connect to Intercom to ingest conversations into ${APP_NAME}`,
  Icon: IntercomIconWrapper,
  ConfigModal: IntercomConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'conversations',
      name: 'Conversations',
      description: 'Import Intercom conversations',
      enabled: true,
    },
  ],

  handleConnect: async ({ workspaceId, features, redirectUrl, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish)
    const targetRedirectUrl = redirectUrl || `${window.location.origin}/auth-callback/Intercom`

    try {
    const data = await trpcClient.integrations.initIntercomAuth.mutate({
      workspaceId,
      features,
      redirectUrl: targetRedirectUrl,
      providerType: 'Intercom',
    })

    if (!data.authUrl) {
      console.error('No Intercom auth URL returned from initialization')
      finish('error')
      return
    }

    openOAuthConnectPopup({
      authUrl: data.authUrl, windowName: 'intercom-auth', provider: 'Intercom',
      onSuccess: result => {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
        onSuccess?.(result);
      },
      onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
      onFinish: finish,
    })
    } catch (error) {
      console.error('Error initializing Intercom auth:', error)
      toast.error(
        `Error initializing Intercom authentication: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
      finish('error')
    }
  },

  onConnectComplete: async ({ code, state }) => {
    const result = await trpcClient.integrations.completeIntercomAuth.mutate({
      code,
      state,
    })

    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    })

    return result
  },

  syncData: async () => {
    await trpcClient.tickets.ingest.mutate({
      platforms: ['INTERCOM'],
    })

    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => Array.isArray(queryKey) && queryKey[0] === 'tickets.list',
    })

    queryClient.invalidateQueries({
      queryKey: trpc.tickets.getIngestionStatus.queryKey({}),
    })
  },

  shouldDisplay: () => true,
}
